import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-terms',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './terms.html',
  styleUrls: ['./terms.css']
})
export class TermsComponent {
  lastUpdated = 'November 27, 2025';

  sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      content: `
        <p>By accessing and using HoneyKode's services, website, and platform (collectively, the "Services"), you agree to be bound by these Terms and Conditions ("Terms"). These Terms constitute a legally binding agreement between you and HoneyKode.</p>

        <h3>1.1 Agreement to Terms</h3>
        <p>Your use of our Services constitutes your acceptance of these Terms. If you do not agree to these Terms, you must not access or use our Services.</p>

        <h3>1.2 Modifications</h3>
        <p>We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of the Services after such changes constitutes acceptance of the modified Terms.</p>

        <h3>1.3 Eligibility</h3>
        <p>You must be at least 18 years old and have the legal capacity to enter into contracts to use our Services. By using our Services, you represent and warrant that you meet these requirements.</p>

        <h3>1.4 Business Use</h3>
        <p>Our Services are primarily intended for business and professional use. If you are using our Services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>
      `
    },
    {
      id: 'services',
      title: 'Services Description',
      content: `
        <p>HoneyKode provides technology services including but not limited to:</p>
        <ul>
          <li>Custom software development and programming services</li>
          <li>Web application design and development</li>
          <li>Mobile application development</li>
          <li>Cloud solutions and infrastructure services</li>
          <li>Technology consulting and advisory services</li>
          <li>System integration and API development</li>
          <li>Maintenance and support services</li>
        </ul>

        <h3>2.1 Service Availability</h3>
        <p>While we strive to provide uninterrupted access to our Services, we do not guarantee that the Services will be available at all times. We may suspend, withdraw, or restrict access to the Services for business and operational reasons.</p>

        <h3>2.2 Service Modifications</h3>
        <p>We reserve the right to modify, suspend, or discontinue any aspect of the Services at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation of the Services.</p>

        <h3>2.3 Service Agreements</h3>
        <p>Specific projects and engagements may be governed by separate service agreements, statements of work, or contracts. In the event of any conflict between these Terms and a separate written agreement, the separate agreement shall prevail.</p>

        <h3>2.4 Third-Party Services</h3>
        <p>Our Services may integrate with or rely on third-party services and platforms. We are not responsible for the availability, functionality, or terms of third-party services.</p>
      `
    },
    {
      id: 'user-obligations',
      title: 'User Obligations',
      content: `
        <h3>3.1 Account Security</h3>
        <p>You are responsible for:</p>
        <ul>
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activities that occur under your account</li>
          <li>Notifying us immediately of any unauthorized use of your account</li>
          <li>Ensuring your account information is accurate and up-to-date</li>
        </ul>

        <h3>3.2 Acceptable Use</h3>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Services for any unlawful or fraudulent purpose</li>
          <li>Violate any applicable laws, regulations, or third-party rights</li>
          <li>Transmit any viruses, malware, or other malicious code</li>
          <li>Attempt to gain unauthorized access to our systems or networks</li>
          <li>Interfere with or disrupt the Services or servers</li>
          <li>Reverse engineer, decompile, or disassemble any aspect of the Services</li>
          <li>Remove or modify any proprietary notices or labels</li>
          <li>Use automated systems to access the Services without authorization</li>
          <li>Impersonate any person or entity or misrepresent your affiliation</li>
          <li>Collect or harvest information about other users</li>
        </ul>

        <h3>3.3 Content Standards</h3>
        <p>Any content you provide must:</p>
        <ul>
          <li>Be accurate and not misleading</li>
          <li>Comply with applicable laws and regulations</li>
          <li>Not infringe on intellectual property rights</li>
          <li>Not contain offensive, defamatory, or inappropriate material</li>
        </ul>

        <h3>3.4 Cooperation</h3>
        <p>You agree to provide timely and accurate information, feedback, and approvals necessary for us to deliver the Services effectively.</p>
      `
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      content: `
        <h3>4.1 HoneyKode's Intellectual Property</h3>
        <p>All intellectual property rights in the Services, including software, designs, trademarks, logos, and content, are owned by or licensed to HoneyKode. These Terms do not grant you any rights to our intellectual property except as expressly stated.</p>

        <h3>4.2 Limited License</h3>
        <p>Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your internal business purposes.</p>

        <h3>4.3 Custom Development Work</h3>
        <p>For custom development projects:</p>
        <ul>
          <li><strong>Work Product:</strong> Upon full payment, you will receive the agreed-upon ownership rights to custom-developed deliverables as specified in the service agreement</li>
          <li><strong>Pre-Existing Materials:</strong> We retain all rights to pre-existing code, frameworks, libraries, and materials used in the development</li>
          <li><strong>Reusable Components:</strong> We retain the right to reuse general programming techniques, methodologies, and non-proprietary code</li>
          <li><strong>Tools and Templates:</strong> Our proprietary tools, templates, and development frameworks remain our property</li>
        </ul>

        <h3>4.4 Your Content</h3>
        <p>You retain all rights to content you provide to us. By providing content, you grant us a license to use, modify, and process it solely to deliver the Services to you.</p>

        <h3>4.5 Feedback</h3>
        <p>If you provide feedback, suggestions, or ideas about our Services, we may use them without any obligation to compensate you.</p>

        <h3>4.6 Copyright Infringement</h3>
        <p>If you believe any content infringes your copyright, please contact us at <a href="mailto:legal@honeykode.com">legal@honeykode.com</a> with details of the alleged infringement.</p>
      `
    },
    {
      id: 'limitation-of-liability',
      title: 'Limitation of Liability',
      content: `
        <h3>5.1 Disclaimer of Warranties</h3>
        <p>THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>

        <h3>5.2 Limitation of Liability</h3>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, HONEYKODE SHALL NOT BE LIABLE FOR:</p>
        <ul>
          <li>INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
          <li>LOSS OF PROFITS, REVENUE, DATA, OR USE</li>
          <li>BUSINESS INTERRUPTION OR LOSS OF BUSINESS OPPORTUNITIES</li>
          <li>DAMAGES ARISING FROM YOUR USE OR INABILITY TO USE THE SERVICES</li>
        </ul>

        <h3>5.3 Maximum Liability</h3>
        <p>IN NO EVENT SHALL HONEYKODE'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.</p>

        <h3>5.4 Exceptions</h3>
        <p>Some jurisdictions do not allow the exclusion or limitation of certain warranties or liabilities. In such cases, our liability will be limited to the maximum extent permitted by law.</p>

        <h3>5.5 Force Majeure</h3>
        <p>We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, natural disasters, war, terrorism, labor disputes, or governmental actions.</p>

        <h3>5.6 Indemnification</h3>
        <p>You agree to indemnify and hold harmless HoneyKode, its officers, directors, employees, and agents from any claims, damages, losses, and expenses (including legal fees) arising from your use of the Services, violation of these Terms, or infringement of any rights of third parties.</p>
      `
    },
    {
      id: 'termination',
      title: 'Termination',
      content: `
        <h3>6.1 Termination by You</h3>
        <p>You may terminate your account at any time by contacting us at <a href="mailto:support@honeykode.com">support@honeykode.com</a>. You remain responsible for all charges incurred prior to termination.</p>

        <h3>6.2 Termination by Us</h3>
        <p>We may suspend or terminate your access to the Services immediately, without prior notice, if:</p>
        <ul>
          <li>You breach these Terms</li>
          <li>You fail to pay applicable fees</li>
          <li>Your use of the Services poses a security risk</li>
          <li>We are required to do so by law</li>
          <li>We discontinue the Services</li>
        </ul>

        <h3>6.3 Effect of Termination</h3>
        <p>Upon termination:</p>
        <ul>
          <li>Your right to access and use the Services immediately ceases</li>
          <li>We may delete your account and associated data</li>
          <li>You remain liable for all outstanding payments</li>
          <li>Provisions that by their nature should survive termination shall continue to apply</li>
        </ul>

        <h3>6.4 Data Retrieval</h3>
        <p>Upon request made within thirty (30) days of termination, we will provide you with access to retrieve your data, subject to technical feasibility and applicable fees.</p>

        <h3>6.5 Ongoing Projects</h3>
        <p>For active development projects, termination shall be governed by the applicable service agreement. Generally, you will be responsible for payment for work completed through the termination date.</p>
      `
    },
    {
      id: 'governing-law',
      title: 'Governing Law and Dispute Resolution',
      content: `
        <h3>7.1 Governing Law</h3>
        <p>These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which HoneyKode operates, without regard to its conflict of law provisions.</p>

        <h3>7.2 Dispute Resolution</h3>
        <p>In the event of any dispute arising from these Terms or the Services:</p>
        <ol>
          <li><strong>Informal Resolution:</strong> We encourage you to contact us first to seek an informal resolution</li>
          <li><strong>Mediation:</strong> If informal resolution fails, parties agree to attempt mediation before pursuing litigation</li>
          <li><strong>Jurisdiction:</strong> Any legal action must be brought in the courts of competent jurisdiction in our operating location</li>
        </ol>

        <h3>7.3 Class Action Waiver</h3>
        <p>You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.</p>

        <h3>7.4 Time Limitation</h3>
        <p>Any cause of action you may have with respect to the Services must be commenced within one (1) year after the claim or cause of action arises.</p>

        <h3>7.5 Injunctive Relief</h3>
        <p>Notwithstanding the above, either party may seek injunctive or equitable relief in any court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights.</p>
      `
    },
    {
      id: 'contact',
      title: 'General Provisions',
      content: `
        <h3>8.1 Entire Agreement</h3>
        <p>These Terms, together with any service agreements and our Privacy Policy, constitute the entire agreement between you and HoneyKode regarding the Services.</p>

        <h3>8.2 Severability</h3>
        <p>If any provision of these Terms is found to be invalid or unenforceable, that provision shall be enforced to the maximum extent permissible, and the remaining provisions shall remain in full force and effect.</p>

        <h3>8.3 Waiver</h3>
        <p>Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.</p>

        <h3>8.4 Assignment</h3>
        <p>You may not assign or transfer these Terms or your rights hereunder without our prior written consent. We may assign these Terms without restriction.</p>

        <h3>8.5 No Agency</h3>
        <p>Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship between you and HoneyKode.</p>

        <h3>8.6 Notices</h3>
        <p>Notices to you may be sent to the email address associated with your account. Notices to us should be sent to <a href="mailto:legal@honeykode.com">legal@honeykode.com</a>.</p>

        <h3>8.7 Export Compliance</h3>
        <p>You agree to comply with all applicable export and import laws and regulations in your use of the Services.</p>

        <h3>8.8 Contact Information</h3>
        <p>For questions about these Terms, please contact us:</p>
        <div class="contact-info">
          <p><strong>HoneyKode</strong></p>
          <p>Email: <a href="mailto:legal@honeykode.com">legal@honeykode.com</a></p>
          <p>General Inquiries: <a href="mailto:info@honeykode.com">info@honeykode.com</a></p>
          <p>Phone: +1 (555) 123-4567</p>
        </div>
      `
    }
  ];
}
